import type {
  AddressAutofillSuggestion,
  SearchBoxSuggestion,
  GeocodingFeature,
} from '@mapbox/search-js-core'
import type {
  GeocodingAddressContext,
  RawUKAddress,
  UnifiedAddressSuggestion,
} from '@/models/address.js'
import type { RatsitAddressResult } from '@/composables/useRatsitLookup.js'
/**
 * Convert a structured UK address object from ideal-postcodes API
 * to UnifiedAddressSuggestion.
 */
export const convertRawUKAddress = (
  address: RawUKAddress,
  postcode: string,
  index: number,
): UnifiedAddressSuggestion => {
  const formattedAddress = [
    address.line_1,
    address.line_2,
    address.line_3,
    address.dependant_locality,
    address.post_town,
    address.county,
  ]
    .filter((part) => part.length > 0)
    .join(', ')

  return {
    id: `uk-postcode-${postcode}-${index}`,
    name: address.line_1 || formattedAddress,
    address: formattedAddress,
    addressLevel1: address.county,
    addressLevel2: address.post_town,
    city: address.post_town,
    placeFormatted: formattedAddress,
    postcode,
    description: formattedAddress,
    country: 'United Kingdom',
    countryCode: 'GB',
    regionCode: address.county,
    regionCodeFull: address.county,
    streetAddress2: address.line_2 || undefined,
    streetAddress3: address.line_3 || undefined,
  }
}

export const sortRawUKAddressesByStreetNumber = (
  addresses: readonly RawUKAddress[],
): RawUKAddress[] =>
  [...addresses].sort((a, b) => {
    const aNum = Number.parseInt(a.line_1.split(' ')[0] || '0', 10)
    const bNum = Number.parseInt(b.line_1.split(' ')[0] || '0', 10)
    return aNum - bNum
  })

export const convertAddressAutofillSuggestion = (
  suggestion: AddressAutofillSuggestion,
): UnifiedAddressSuggestion => {
  // The Mapbox Address Autofill API returns `region_code` and
  // `metadata.iso_3166_2` (documented at docs.mapbox.com/api/search/search),
  // but @mapbox/search-js-core@1.5.1 omits them from AddressAutofillSuggestion.
  // The same fields are correctly typed in SearchBoxSuggestion and GeocodingFeature.
  const metadata = suggestion.metadata as {
    iso_3166_1: string
    iso_3166_2?: string
  }
  const regionCode =
    (suggestion as unknown as { region_code?: string }).region_code || ''
  const regionCodeFull = metadata.iso_3166_2 || ''

  return {
    id: suggestion.mapbox_id,
    // address_line1 follows WHATWG Autocomplete Specification and respects
    // locale conventions (e.g. "Calle Celso 4" for ES vs "123 Main St" for US),
    // while feature_name always uses anglicized number-first ordering.
    name: suggestion.address_line1 || suggestion.feature_name,
    placeFormatted: suggestion.description,
    postcode: suggestion.postcode || '',
    description: suggestion.description,
    address: suggestion.address || '',
    addressLevel1: suggestion.address_level1 || '',
    addressLevel2: suggestion.address_level2 || '',
    city: suggestion.address_level2 || '',
    country: suggestion.country || '',
    countryCode: suggestion.country_code || '',
    regionCode,
    regionCodeFull,
  }
}

export const convertSearchBoxSuggestion = (
  suggestion: SearchBoxSuggestion,
): UnifiedAddressSuggestion => ({
  id: suggestion.mapbox_id,
  name: suggestion.name,
  address: suggestion.address || '',
  placeFormatted: suggestion.place_formatted,
  postcode:
    suggestion.feature_type === 'postcode'
      ? suggestion.name
      : suggestion.context.postcode?.name || '',
  description: suggestion.place_formatted,
  addressLevel2: suggestion.context.place?.name || '',
  addressLevel1: suggestion.context.region?.name || '',
  city: suggestion.context.place?.name || '',
  country: suggestion.context.country?.name || '',
  countryCode: suggestion.context.country?.country_code || '',
  regionCode: suggestion.context.region?.region_code || '',
  regionCodeFull: suggestion.context.region?.region_code_full || '',
})

export const convertGeocodingFeature = (
  feature: GeocodingFeature,
): UnifiedAddressSuggestion => ({
  id:
    feature.id ||
    `${feature.geometry.coordinates[0]}-${feature.geometry.coordinates[1]}`,
  name: feature.properties.name || feature.properties.name_preferred || '',
  address:
    feature.properties.full_address || feature.properties.place_formatted || '',
  placeFormatted:
    feature.properties.place_formatted || feature.properties.full_address || '',
  postcode: feature.properties.context?.postcode?.name || '',
  description:
    feature.properties.place_formatted || feature.properties.full_address || '',
  addressLevel2: feature.properties.place || '',
  addressLevel1: feature.properties.region || '',
  city: feature.properties.context?.place?.name || '',
  country: feature.properties.context?.country?.name || '',
  countryCode: feature.properties.context?.country?.country_code || '',
  regionCode: feature.properties.context?.region?.region_code || '',
  regionCodeFull: feature.properties.context?.region?.region_code_full || '',
})

/**
 * Extract the street name (without house number) from a Mapbox GeocodingFeature.
 * Uses context fields that exist at runtime but aren't in the SDK types.
 *
 * Priority:
 * 1. context.address.street_name (most reliable for address-type features)
 * 2. context.street.name (reliable for street-type features)
 * 3. properties.name (fallback, may include house number)
 */
export const extractStreetName = (feature: GeocodingFeature): string => {
  const addressContext = feature.properties.context?.address as
    | GeocodingAddressContext
    | undefined

  if (addressContext?.street_name) {
    return addressContext.street_name
  }

  const streetContext = feature.properties.context?.street
  if (streetContext?.name) {
    return streetContext.name
  }

  return feature.properties.name || feature.properties.name_preferred || ''
}

/**
 * Convert a GeocodingFeature to UnifiedAddressSuggestion with the house number
 * stripped from name. Used for NL postcode lookup where users
 * enter house numbers separately.
 */
export const convertGeocodingFeatureStreetOnly = (
  feature: GeocodingFeature,
): UnifiedAddressSuggestion => {
  const suggestion = convertGeocodingFeature(feature)
  const streetName = extractStreetName(feature)

  return {
    ...suggestion,
    name: streetName,
  }
}

/**
 * Deduplicate UnifiedAddressSuggestion entries by name (case-insensitive).
 * Keeps the first occurrence. Useful when house-number stripping produces
 * duplicate street entries.
 */
export const deduplicateSuggestionsByName = (
  suggestions: UnifiedAddressSuggestion[],
): UnifiedAddressSuggestion[] => {
  const seen = new Set<string>()
  return suggestions.filter((suggestion) => {
    const key = suggestion.name.toLowerCase()
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

/**
 * Converts a bare state code to ISO 3166-2 format by prepending the country.
 * e.g. toISOStateCode("FL", "US") → "US-FL".
 * Already-prefixed values are returned unchanged.
 */
export const toISOStateCode = (state?: string, country?: string): string => {
  if (!state) {
    return ''
  }
  if (!country) {
    return state
  }
  return state.includes('-') ? state : `${country}-${state}`
}

/**
 * Extracts the bare state code from an ISO 3166-2 formatted string.
 * e.g. "US-FL" → "FL", "FL" → "FL", undefined → undefined
 */
export const fromISOStateCode = (
  state: string | undefined,
): string | undefined => state?.split('-').pop()

export const normalizeRatsitAddressResult = (
  address: RatsitAddressResult,
): string | undefined => {
  const parts = [
    address.street || '',
    address.zipCode || '',
    address.city || '',
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : undefined
}
